import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import Layout from '../components/Layout';
import {
  Briefcase, Users, CheckCircle, TrendingUp,
  Plus, Eye, Clock, AlertCircle, ArrowRight,
  Lock, FileText, RefreshCw
} from 'lucide-react';
import { STATUS_COLORS, STATUS_LABELS } from '../lib/constants';

export default function Dashboard() {
  const { company, refreshCompany } = useAuth();
  const [stats, setStats] = useState({
    activeJobs: 0, totalApplications: 0,
    shortlisted: 0, hired: 0,
  });
  const [recentApps, setRecentApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const isApproved = company?.verification_status === 'verified';
  const isPending = company?.verification_status === 'pending';
  const isRejected = company?.verification_status === 'rejected';
  const isUnderReview = company?.verification_status === 'under_review';

  useEffect(() => {
    if (!company) return;

    loadData();
    loadNotifications();

    const sub = supabase
      .channel('company-status')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'companies',
        filter: `id=eq.${company.id}`,
      }, () => {
        refreshCompany();
      })
      .subscribe();

    return () => supabase.removeChannel(sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const loadData = async () => {
    if (!isApproved) {
      setLoading(false);
      return;
    }
    try {
      const [jobsRes, appsRes] = await Promise.all([
        supabase.from('job_listings').select('*')
          .eq('company_id', company.id)
          .eq('is_active', true)
          .gte('deadline', new Date().toISOString())
          .order('created_at', { ascending: false }),
        supabase.from('job_applications').select(`
          *,
          student_career_profiles(full_name, degree, university_name),
          job_listings(title)
        `).eq('company_id', company.id)
          .order('applied_at', { ascending: false })
          .limit(6),
      ]);

      const jobsList = jobsRes.data || [];
      const appsList = appsRes.data || [];

      setJobs(jobsList);
      setRecentApps(appsList);
      setStats({
        activeJobs: jobsList.length,
        totalApplications: appsList.length,
        shortlisted: appsList.filter(a =>
          ['shortlisted', 'interview_scheduled'].includes(a.status)
        ).length,
        hired: appsList.filter(a => a.status === 'hired').length,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const { data } = await supabase
        .from('company_notifications')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false })
        .limit(5);
      setNotifications(data || []);

      // Mark as read
      if (data?.some(n => !n.is_read)) {
        await supabase.from('company_notifications')
          .update({ is_read: true })
          .eq('company_id', company.id)
          .eq('is_read', false);
      }
    } catch (_) {}
  };

  const slotsUsed = company?.slots_used || 0;
  const slotsTotal = company?.slots_total || 1;

  return (
    <Layout>
      {/* ── PENDING BANNER ─────────────────────── */}
      {isPending && (
        <div style={{
          background: 'linear-gradient(135deg,#FFF7ED,#FFFBEB)',
          border: '1.5px solid #FED7AA',
          borderRadius: 16, padding: '20px 24px',
          marginBottom: 24,
          display: 'flex', alignItems: 'flex-start',
          gap: 16,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: '#FEF3C7',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
          }}>
            <Clock size={24} color="#D97706" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontWeight: 800, fontSize: 16,
              color: '#92400E', marginBottom: 4,
            }}>
              Application Under Review ⏳
            </div>
            <div style={{
              fontSize: 13, color: '#B45309', lineHeight: 1.6,
            }}>
              Our admin team is reviewing your company information
              and registration documents. This typically takes
              24–48 hours. You'll be notified here as soon as
              a decision is made.
            </div>
            <div style={{
              display: 'flex', gap: 8, marginTop: 12,
              flexWrap: 'wrap',
            }}>
              {[
                '✓ Account Created',
                '🔍 Documents Under Review',
                '⏳ Awaiting Approval',
                '🔒 Posting Locked',
              ].map((s, i) => (
                <span key={i} style={{
                  fontSize: 11, fontWeight: 600,
                  padding: '4px 10px', borderRadius: 6,
                  background: i === 2
                    ? 'rgba(217,119,6,0.15)'
                    : i === 3
                      ? 'rgba(239,68,68,0.1)'
                      : 'rgba(16,185,129,0.1)',
                  color: i === 2 ? '#D97706'
                    : i === 3 ? '#DC2626' : '#059669',
                }}>{s}</span>
              ))}
            </div>
          </div>
          <button onClick={() => { refreshCompany(); loadNotifications(); }}
            style={{
              background: '#FEF3C7', border: '1px solid #FDE68A',
              borderRadius: 8, padding: '6px 10px',
              cursor: 'pointer', color: '#D97706',
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 12, fontWeight: 600,
            }}>
            <RefreshCw size={13} />Refresh
          </button>
        </div>
      )}

      {/* ── REJECTED BANNER ────────────────────── */}
      {isRejected && (
        <div style={{
          background: '#FFF1F2', border: '1.5px solid #FECDD3',
          borderRadius: 16, padding: '20px 24px',
          marginBottom: 24,
          display: 'flex', gap: 16, alignItems: 'flex-start',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: '#FFE4E6',
            display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0,
          }}>
            <AlertCircle size={24} color="#E11D48" />
          </div>
          <div>
            <div style={{
              fontWeight: 800, fontSize: 16,
              color: '#9F1239', marginBottom: 6,
            }}>Application Not Approved</div>
            <div style={{
              fontSize: 13, color: '#BE123C',
              lineHeight: 1.6, marginBottom: 10,
            }}>
              {company?.rejection_reason ||
                'Your application did not meet our requirements at this time.'}
            </div>
            <div style={{
              fontSize: 12, color: '#94A3B8',
            }}>
              Please review the reason above, make the necessary
              changes, and contact us at{' '}
              <a href="mailto:admin@inncatechnologies.com"
                style={{ color: '#4F8EF7' }}>
                admin@inncatechnologies.com
              </a>{' '}
              to reapply.
            </div>
          </div>
        </div>
      )}

      {/* ── UNDER REVIEW BANNER ─────────────────── */}
      {isUnderReview && (
        <div style={{
          background: '#EFF6FF', border: '1.5px solid #BFDBFE',
          borderRadius: 16, padding: '20px 24px',
          marginBottom: 24,
          display: 'flex', gap: 16, alignItems: 'center',
        }}>
          <div style={{ fontSize: 32 }}>🔍</div>
          <div>
            <div style={{
              fontWeight: 800, fontSize: 15, color: '#1E40AF',
            }}>
              Active Review in Progress
            </div>
            <div style={{ fontSize: 13, color: '#3B82F6', marginTop: 3 }}>
              Our team is actively reviewing your documents.
              Expect a decision very soon.
            </div>
          </div>
        </div>
      )}

      {/* ── NOTIFICATIONS ──────────────────────── */}
      {notifications.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          {notifications.filter(n => n.type === 'verification').map(n => (
            <div key={n.id} style={{
              background: n.title.includes('approved')
                ? 'linear-gradient(135deg,#F0FDF4,#ECFDF5)'
                : '#FFF1F2',
              border: n.title.includes('approved')
                ? '1.5px solid #BBF7D0'
                : '1.5px solid #FECDD3',
              borderRadius: 14, padding: '16px 20px',
              marginBottom: 8,
              display: 'flex', gap: 14, alignItems: 'center',
            }}>
              <span style={{ fontSize: 24 }}>
                {n.title.includes('approved') ? '🎉' : '📋'}
              </span>
              <div>
                <div style={{
                  fontWeight: 700, fontSize: 14,
                  color: n.title.includes('approved')
                    ? '#065F46' : '#9F1239',
                }}>{n.title}</div>
                <div style={{
                  fontSize: 12, color: '#94A3B8', marginTop: 2,
                }}>{n.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── APPROVED DASHBOARD ─────────────────── */}
      {isApproved ? (
        loading ? (
          <div style={{
            display: 'flex', justifyContent: 'center',
            alignItems: 'center', minHeight: 300,
          }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'flex-start', marginBottom: 28,
              flexWrap: 'wrap', gap: 12,
            }}>
              <div>
                <h1 style={{
                  fontSize: 24, fontWeight: 800, color: '#1E293B',
                }}>
                  Welcome back, {company?.contact_name?.split(' ')[0] || 'there'}! 👋
                </h1>
                <p style={{ color: '#94A3B8', marginTop: 4, fontSize: 14 }}>
                  {company?.name} · Here's your hiring overview
                </p>
              </div>
              <Link to="/post-job" style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '11px 20px',
                background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                borderRadius: 12, color: '#fff',
                fontWeight: 700, fontSize: 14,
                textDecoration: 'none',
                boxShadow: '0 4px 14px rgba(79,142,247,0.3)',
              }}>
                <Plus size={16} />Post a Job
              </Link>
            </div>

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
              gap: 16, marginBottom: 24,
            }}>
              {[
                { icon: <Briefcase size={22} />, label: 'Active Jobs', value: stats.activeJobs, color: '#4F8EF7', bg: 'rgba(79,142,247,0.08)' },
                { icon: <Users size={22} />, label: 'Applications', value: stats.totalApplications, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)' },
                { icon: <CheckCircle size={22} />, label: 'Shortlisted', value: stats.shortlisted, color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
                { icon: <TrendingUp size={22} />, label: 'Hired', value: stats.hired, color: '#22D3EE', bg: 'rgba(34,211,238,0.08)' },
              ].map(s => (
                <div key={s.label} style={{
                  background: '#fff', borderRadius: 16,
                  padding: '20px 22px',
                  border: '1px solid #E2E8F0',
                  display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: s.bg, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    color: s.color, flexShrink: 0,
                  }}>{s.icon}</div>
                  <div>
                    <div style={{
                      fontSize: 28, fontWeight: 800,
                      color: '#1E293B', lineHeight: 1,
                    }}>{s.value}</div>
                    <div style={{
                      fontSize: 12, color: '#94A3B8', marginTop: 4,
                    }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Slot usage */}
            <div style={{
              background: '#fff', borderRadius: 16,
              padding: '20px 24px', border: '1px solid #E2E8F0',
              marginBottom: 24,
            }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: 10,
              }}>
                <div>
                  <div style={{
                    fontWeight: 700, color: '#1E293B', fontSize: 14,
                  }}>Job Slot Usage</div>
                  <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
                    {slotsUsed} of {slotsTotal} slots used
                  </div>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  padding: '4px 10px', borderRadius: 6,
                  background: '#EEF2FF', color: '#4F8EF7',
                }}>
                  {(company?.plan || 'trial').toUpperCase()}
                </span>
              </div>
              <div style={{
                height: 8, background: '#F1F5F9',
                borderRadius: 4, overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%',
                  width: `${Math.min((slotsUsed / slotsTotal) * 100, 100)}%`,
                  background: slotsUsed >= slotsTotal
                    ? '#EF4444'
                    : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                  borderRadius: 4, transition: 'width 0.5s',
                }} />
              </div>
            </div>

            {/* Jobs + Applications */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr', gap: 20,
            }}>
              {/* Active Jobs */}
              <div style={{
                background: '#fff', borderRadius: 16,
                padding: 24, border: '1px solid #E2E8F0',
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 16,
                }}>
                  <div style={{
                    fontWeight: 700, color: '#1E293B', fontSize: 14,
                  }}>Active Jobs</div>
                  <Link to="/post-job" style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 12, color: '#4F8EF7', fontWeight: 600,
                  }}>
                    <Plus size={13} />Post New
                  </Link>
                </div>
                {jobs.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '28px 0',
                    color: '#94A3B8', fontSize: 13,
                  }}>
                    <Briefcase size={32} style={{
                      margin: '0 auto 8px', display: 'block',
                      opacity: 0.3,
                    }} />
                    No active jobs yet.{' '}
                    <Link to="/post-job" style={{
                      color: '#4F8EF7', fontWeight: 600,
                    }}>Post your first job</Link>
                  </div>
                ) : jobs.slice(0, 4).map(j => (
                  <div key={j.id} style={{
                    display: 'flex', alignItems: 'center',
                    gap: 12, paddingBottom: 12, marginBottom: 12,
                    borderBottom: '1px solid #F1F5F9',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: 'rgba(79,142,247,0.1)',
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Briefcase size={16} color="#4F8EF7" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600, fontSize: 13,
                        color: '#1E293B', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{j.title}</div>
                      <div style={{
                        fontSize: 11, color: '#94A3B8',
                        display: 'flex', gap: 6,
                      }}>
                        <Eye size={10} />{j.views_count || 0} views ·
                        <Users size={10} />{j.applications_count || 0}
                      </div>
                    </div>
                    <div style={{
                      fontSize: 11, color: '#EF4444',
                      display: 'flex', alignItems: 'center', gap: 3,
                    }}>
                      <Clock size={10} />
                      {Math.max(0, Math.floor(
                        (new Date(j.deadline) - new Date())
                        / 86400000
                      ))}d
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent Applications */}
              <div style={{
                background: '#fff', borderRadius: 16,
                padding: 24, border: '1px solid #E2E8F0',
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', marginBottom: 16,
                }}>
                  <div style={{
                    fontWeight: 700, color: '#1E293B', fontSize: 14,
                  }}>Recent Applications</div>
                  <Link to="/applications" style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 12, color: '#4F8EF7', fontWeight: 600,
                  }}>
                    View all <ArrowRight size={13} />
                  </Link>
                </div>
                {recentApps.length === 0 ? (
                  <div style={{
                    textAlign: 'center', padding: '28px 0',
                    color: '#94A3B8', fontSize: 13,
                  }}>
                    <Users size={32} style={{
                      margin: '0 auto 8px', display: 'block',
                      opacity: 0.3,
                    }} />
                    No applications yet
                  </div>
                ) : recentApps.slice(0, 5).map(a => {
                  const p = a.student_career_profiles;
                  const sc = STATUS_COLORS[a.status] || '#94A3B8';
                  return (
                    <div key={a.id} style={{
                      display: 'flex', alignItems: 'center',
                      gap: 10, paddingBottom: 10, marginBottom: 10,
                      borderBottom: '1px solid #F1F5F9',
                    }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', color: '#fff',
                        fontWeight: 800, fontSize: 13, flexShrink: 0,
                      }}>
                        {(p?.full_name || 'U')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 600, fontSize: 13,
                          color: '#1E293B', overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{p?.full_name || 'Student'}</div>
                        <div style={{
                          fontSize: 11, color: '#94A3B8',
                          overflow: 'hidden', textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>{a.job_listings?.title}</div>
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: '3px 7px', borderRadius: 5,
                        background: `${sc}18`, color: sc,
                        flexShrink: 0,
                      }}>
                        {STATUS_LABELS[a.status]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )
      ) : (
        /* ── NON-APPROVED STATE ───────────────── */
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))',
          gap: 16,
        }}>
          {[
            {
              icon: <FileText size={24} color="#4F8EF7" />,
              bg: 'rgba(79,142,247,0.08)',
              title: 'Company Profile',
              desc: 'View and update your company information',
              link: '/profile',
              label: 'Edit Profile',
              color: '#4F8EF7',
            },
            {
              icon: <Lock size={24} color="#94A3B8" />,
              bg: 'rgba(148,163,184,0.08)',
              title: 'Post a Job',
              desc: 'Available after account approval',
              link: null,
              label: 'Locked',
              color: '#94A3B8',
              locked: true,
            },
            {
              icon: <Lock size={24} color="#94A3B8" />,
              bg: 'rgba(148,163,184,0.08)',
              title: 'Applications',
              desc: 'Available after account approval',
              link: null,
              label: 'Locked',
              color: '#94A3B8',
              locked: true,
            },
          ].map((item, i) => (
            <div key={i} style={{
              background: '#fff', borderRadius: 16,
              padding: 24, border: '1px solid #E2E8F0',
              opacity: item.locked ? 0.6 : 1,
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: item.bg,
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', marginBottom: 14,
              }}>
                {item.icon}
              </div>
              <div style={{
                fontWeight: 700, fontSize: 15,
                color: '#1E293B', marginBottom: 6,
              }}>{item.title}</div>
              <div style={{
                fontSize: 13, color: '#94A3B8',
                marginBottom: 16, lineHeight: 1.5,
              }}>{item.desc}</div>
              {item.link ? (
                <Link to={item.link} style={{
                  display: 'inline-flex', alignItems: 'center',
                  gap: 6, padding: '8px 16px',
                  background: `${item.color}12`,
                  border: `1px solid ${item.color}30`,
                  borderRadius: 8, color: item.color,
                  fontWeight: 600, fontSize: 12,
                  textDecoration: 'none',
                }}>
                  {item.label} <ArrowRight size={13} />
                </Link>
              ) : (
                <span style={{
                  display: 'inline-flex', alignItems: 'center',
                  gap: 6, padding: '8px 16px',
                  background: '#F1F5F9', borderRadius: 8,
                  color: '#94A3B8', fontWeight: 600, fontSize: 12,
                }}>
                  <Lock size={12} />{item.label}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}